import {
  Component,
  HostBinding,
  OnDestroy,
  OnInit,
  Self,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Meta, Title } from '@angular/platform-browser';
import {
  EMPTY,
  Subject,
  catchError,
  finalize,
  take,
  throwError,
} from 'rxjs';
import { ApiResponse } from '../../models/api-response.interface';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { CancelData } from '../../models/cancel-data.model';
import { NotificationsService } from 'angular2-notifications';
import { ContractsApiService } from 'src/app/core/services/api/contracts-api.service';
import { Auto_Complete } from '../../../core/services/auto-complete';
import { HttpService } from '../../../core/services/api/http.service';
import { ErrorHandlingService } from '../../../core/services/error-handling.service';
import { Contract } from '../../models/contract.interface';
import { HttpHeaders } from '@angular/common/http';
import { CancelCalculation } from '../../models/cancel-calculations.model';
import { API_VERSIONS } from '../../consts/api-versions.const';
import { UntilDestroy } from '@ngneat/until-destroy';
import { QuoteDataComponent } from '../quote-data/quote-data.component';
import { jsPDF } from 'jspdf';
import {
  CancelQuoteBranding,
  getCancelQuoteBranding,
} from './cancel-quote-branding.config';

@UntilDestroy()
@Component({
  selector: 'app-cancel-quote',
  templateUrl: './cancel-quote.component.html',
  styleUrls: ['./cancel-quote.component.css'],
  providers: [FormBuilder],
})
export class CancelQuoteComponent implements OnInit, OnDestroy {
  branding?: CancelQuoteBranding;
  isLoading: boolean = false;
  quoteForm!: FormGroup;
  cancelQuote?: CancelCalculation;
  contracts: any[] = [];
  isQuoteSent: boolean = false;
  isContractSelected: boolean = false;
  isContractFieldActive: boolean = false;
  isContractFieldTriggered: boolean = false;
  isTermsChecked: boolean = false;
  quoteGeneratedAt?: Date;
  @ViewChild(QuoteDataComponent) quoteDataComponent?: QuoteDataComponent;
  private readonly destroy$ = new Subject<void>();
  protected readonly ADMIN_DOMEIN = '@dealerloyaltyprotection.com';
  contractsFilter: Auto_Complete = new Auto_Complete(
    this._api,
    this.errHandle,
    '/global-contract-search'
  );

  cancellationReasonOptions: any[] = [];
  acceptedFormats = '.pdf, .doc, .docx, .jpg, .jpeg, .png, .csv, .xls, .xlsx';

  private getCancellationReasonOptions(tenant: string) {
    this._api
      .getReq(
        `/${API_VERSIONS.CANCELLATIONS}/cancellation/reasons?for_claims=0&filter_by_status=1&length=100`,
        {
          headers: new HttpHeaders({
            'X-Tenant': tenant,
          }),
        }
      )
      .subscribe(
        (res: any) => {
          this.cancellationReasonOptions = res.data;
        },
        (error) => {
          this.errHandle.errorHandling(error);
        }
      );
  }

  searchContractOptions = [
    { label: 'Contract Number', key: 'search_by_contract_number_exact_match' },
    { label: 'VIN Number', key: 'search_by_vin_number_exact_match' },
  ];
  selectedContractOption = this.searchContractOptions[0]?.key;
  private readonly contractSelectionValidator: ValidatorFn = (
    control: AbstractControl
  ): ValidationErrors | null => {
    const value = control.value;
    if (value && typeof value === 'object' && value.id) {
      return null;
    }

    return { contractNotSelected: true };
  };

  @HostBinding('class')
  get themeClass(): string {
    return this.branding?.themeClass ?? '';
  }

  @HostBinding('style.--color-primary')
  get primaryColor(): string | undefined {
    return this.branding?.primaryColor;
  }

  @HostBinding('style.--main-color')
  get mainColor(): string | undefined {
    return this.branding?.primaryColor;
  }

  @HostBinding('style.--color-primary-hover')
  get primaryColorHover(): string | undefined {
    return this.branding?.primaryColorHover;
  }

  @HostBinding('style.--main-color-hover')
  get mainColorHover(): string | undefined {
    return this.branding?.primaryColorHover;
  }

  @HostBinding('style.--color-primary-tint')
  get primaryColorTint(): string | undefined {
    return this.branding?.primaryColorLight;
  }

  @HostBinding('style.--main-color-light')
  get mainColorLight(): string | undefined {
    return this.branding?.primaryColorLight;
  }

  constructor(
    private contractApiService: ContractsApiService,
    private notificationService: NotificationsService,
    private _api: HttpService,
    private errHandle: ErrorHandlingService,
    private activatedRoute: ActivatedRoute,
    private meta: Meta,
    private title: Title,
    @Self() private fb: FormBuilder
  ) {}

  get canOverrideDate(): boolean {
    return (this.quoteForm.get('email')?.value as string)?.endsWith(
      this.ADMIN_DOMEIN
    );
  }

  get canceledAtInputValue(): string {
    const value = this.quoteForm?.get('canceled_at')?.value;
    if (!value) return '';

    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return '';

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  get quoteReferenceControl(): FormControl {
    return this.quoteForm.get('quote_reference') as FormControl;
  }

  ngOnInit(): void {
    this.contractsFilter.suppressErrorToast = true;
    this.contractsFilter.searchBy = this.selectedContractOption;
    this.contractsFilter.options = {
      is_not_cancelled: 1,
      filter_by_statuses: 'active,paid',
    };
    this.initForm();
    this.applyBrandingFromRoute();
  }

  private applyBrandingFromRoute(): void {
    const tenantParam = this.activatedRoute.snapshot.paramMap.get('tenant');
    this.branding = getCancelQuoteBranding(tenantParam);

    if (this.branding) {
      this.applyShareMetadata();
      this.getCancellationReasonOptions(this.branding.tenant);
      return;
    }

    this.branding = getCancelQuoteBranding('fi');
    const hostnameTenant = this.resolveTenantFromHostname();
    this.getCancellationReasonOptions(hostnameTenant || this.branding?.tenant || 'dlp');
  }

  private applyShareMetadata(): void {
    if (!this.branding) return;

    const pageTitle = `${this.branding.logoAlt} Cancellation Quote`;
    const description = `Get a cancellation quote from ${this.branding.logoAlt}.`;
    const imageUrl = new URL(
      this.branding.logoSrc.replace(/^\.\//, ''),
      window.location.origin
    ).href;

    this.title.setTitle(pageTitle);
    this.meta.updateTag({ property: 'og:title', content: pageTitle });
    this.meta.updateTag({ property: 'og:description', content: description });
    this.meta.updateTag({ property: 'og:image', content: imageUrl });
    this.meta.updateTag({ property: 'og:image:secure_url', content: imageUrl });
    this.meta.updateTag({ property: 'og:image:type', content: 'image/png' });
    this.meta.updateTag({ property: 'og:image:width', content: '300' });
    this.meta.updateTag({ property: 'og:image:height', content: '300' });
    this.meta.updateTag({ name: 'twitter:title', content: pageTitle });
    this.meta.updateTag({ name: 'twitter:description', content: description });
    this.meta.updateTag({ name: 'twitter:image', content: imageUrl });
  }

  private resolveTenantFromHostname(): string | undefined {
    const hostnameParts = window.location.hostname.split('.');

    if (
      hostnameParts.length > 2 &&
      hostnameParts[0] !== 'dashboard'
    ) {
      return hostnameParts[0];
    }

    if (
      window.location.hostname.includes('localhost') &&
      hostnameParts.length > 1
    ) {
      return hostnameParts[0];
    }

    return undefined;
  }

  onContractSearchChange() {
    this.contractsFilter.searchBy = this.selectedContractOption;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  handleFormSubmit(): void {
    if (this.quoteForm.invalid) {
      this.quoteForm.markAllAsTouched();
      return;
    }

    this.isLoading = true;
    const formValue = this.quoteForm.value;
    const cancelData = new CancelData({
      contract_id: formValue.contract.id,
      reason_id: formValue.reason_id,
      canceled_at: formValue.canceled_at,
      odometer: formValue.odometer,
      email: formValue.email,
    });

    const headers = new HttpHeaders({
      'X-Tenant': this.quoteForm.value.contract.domain,
    });

    this.contractApiService
      .calculateCancellation(cancelData, headers)
      .pipe(
        take(1),
        finalize(() => (this.isLoading = false)),
        catchError(({ error }) => {
          this.notificationService.error('Error', error?.message);
          Object.keys(cancelData).forEach((key: string) => {
            if (Object.keys(error?.error).includes(key)) {
              this.notificationService.error('Error', error?.error[key]);
            }
          });
          return EMPTY;
        })
      )
      .subscribe((response: ApiResponse<CancelCalculation>) => {
        if (!response.data) return;
        this.cancelQuote = response.data;
        this.quoteGeneratedAt = new Date();
      });
  }

  handleQuoteReset(): void {
    this.quoteForm.reset();
    this.cancelQuote = undefined;
    this.quoteGeneratedAt = undefined;
    this.isTermsChecked = false;
  }

  handleQuoteSend(): void {
    const selectedContract = this.quoteForm?.value?.contract;

    if (!selectedContract) return;
    this.isLoading = true;
    const formValue = this.quoteForm.value;

    const formData = new FormData();
    formData.append('reason_id', formValue.reason_id);
    formData.append('canceled_at', formValue.canceled_at.toISOString());
    formData.append('description', '');
    formData.append('cancellation_mileage', formValue.odometer);
    formData.append(
      'is_active',
      formValue.contract.status === 'active' ? '1' : '0'
    );
    formData.append('email', formValue.email);

    if (formValue?.files?.length) {
      formValue.files.forEach((file: File) => {
        formData.append('attachment', file);
      });
    }

    const headers = new HttpHeaders({
      'X-Tenant': this.quoteForm.value.contract.domain,
    });

    this.contractApiService
      .sendCancelQuote(selectedContract.id, formData, headers)
      .pipe(
        take(1),
        catchError(({ error }) => {
          this.notificationService.error('Error', error.message);
          return throwError(() => error);
        }),
        finalize(() => (this.isLoading = false))
      )
      .subscribe((result: ApiResponse) => {
        this.handleQuoteReset();
        this.notificationService.success('Success', result.message);
        this.isQuoteSent = true;
      });
  }

  handleCanceledAtInput(event: Event): void {
    const value = (event.target as HTMLInputElement).value;
    const control = this.quoteForm.get('canceled_at');

    control?.setValue(value ? new Date(`${value}T00:00:00`) : null);
    control?.markAsTouched();
  }

  handlePrint(): void {
    const printElement = document.getElementById('cancel-quote-print-section');
    if (!printElement) return;

    const doc = new jsPDF();
    const elementHTML = document.createElement('div');
    elementHTML.innerHTML = printElement.innerHTML;
    elementHTML.removeAttribute('hidden');
    elementHTML.style.letterSpacing = '0.03px';
    elementHTML.style.width = '100%';
    elementHTML.style.transform = 'scale(1)';
    elementHTML.style.transformOrigin = 'top left';

    doc.html(elementHTML, {
      callback: function (doc) {
        window.open(doc.output('bloburl'), '_blank');
      },
      x: 5,
      y: 5,
      width: 190,
      windowWidth: 1500,
    });
  }

  getUloadTitle(): string {
    let title;
    switch (this.quoteForm.value.reason_id) {
      case 28:
      case 42:
        title =
          'Signed Cancellation form from the customer authorizing a cancellation.';
        break;
      case 30:
      case 44:
        title = 'Documents supporting that a repossession has taken place.';
        break;
      case 7:
      case 27:
      case 41:
        title =
          'Signed trade in form along with a signed Odometer reading at the time of trade in or sale.';
        break;
      case 26:
      case 40:
        title =
          'Documents deeming the vehicle a Total loss from the insurance company or lienholder.';
        break;
      case 36:
      case 15:
        title =
          'Documents supporting and showing a zero balance after the vehicle is paid off.';
        break;
      default:
        title =
          'Upload supporting documents. Text explaining what document needs to be updated';
        break;
    }
    return title;
  }

  handleContractQueryChange(event: any) {
    this.isContractSelected = false;
    this.isContractFieldTriggered = true;
    this.contractsFilter.onQueryChange(event);
  }

  handleSelectContract(event: any) {
    this.isContractSelected = true;
    this.contractsFilter.onSelected(event);
    this.getCancellationReasonOptions(event.domain);
  }

  handleContractFocus(): void {
    this.isContractFieldActive = true;
    this.isContractFieldTriggered = true;
  }

  handleContractBlur(): void {
    this.isContractFieldActive = false;
    this.isContractFieldTriggered = true;
    this.quoteForm.get('contract')?.markAsTouched();
  }

  handleContractChange(contract: Contract): void {
    if (contract?.id) {
      this.isContractSelected = true;
    }

    const reasonControl = this.quoteForm.get('reason_id');
    if (contract?.id) {
      reasonControl?.enable();
    } else {
      reasonControl?.disable();
    }

    reasonControl?.addValidators([Validators.required]);
    reasonControl?.setValue(null);
  }

  private initForm(): void {
    this.quoteForm = this.fb.group({
      contract: [
        null,
        [Validators.required, this.contractSelectionValidator],
      ],
      quote_reference: [null, []],
      canceled_at: [null, Validators.required],
      odometer: [null, [Validators.min(0), Validators.required]],
      reason_id: { value: [null, Validators.required], disabled: true },
      email: [null, [Validators.required, Validators.email]],
      files: [null],
    });
    this.quoteForm.valueChanges.subscribe((value) => {});
  }

  handleLoadQuote() {
    this.contractApiService
      .loadQuote(this.quoteForm.get('quote_reference')?.value)
      .subscribe((res: any) => {
        res.data.contract.domain = res.data.domain;
        this.quoteForm.patchValue({
          contract: res.data.contract,
          canceled_at: new Date(res.data.canceled_at),
          odometer: res.data.cancellation_mileage,
          reason_id: res.data.reason_id,
          email: res.data.email,
        });
        this.isContractSelected = true;
        this.handleFormSubmit();
      });
  }
}
